import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Bell, Plus, Send, Calendar, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  platformBroadcastsApi, PlatformBroadcast, BroadcastScope, BroadcastSeverity,
  BackendPlanTier, tierToLabel,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";

const TIERS: BackendPlanTier[] = ["BASIC", "PRO", "PREMIUM", "ENTERPRISE"];
const sevStyle: Record<string, string> = {
  CRITICAL: "border-destructive/30 bg-destructive/10 text-destructive",
  WARNING: "border-warning/30 bg-warning/10 text-warning",
  INFO: "border-info/30 bg-info/10 text-info",
};
const scopeLabel: Record<BroadcastScope, string> = {
  ALL_TENANTS: "All tenants", SELECTED_TENANTS: "Selected tenants", PLAN_TARGETED: "Plan-targeted", INTERNAL: "Internal",
};
const statusStyle = (s: string) =>
  s === "SENT" ? "border-success/30 bg-success/10 text-success"
    : s === "SCHEDULED" ? "border-primary/30 bg-primary/10 text-primary"
    : "border-muted-foreground/30";

export default function GlobalNotifications() {
  const [rows, setRows] = useState<PlatformBroadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    platformBroadcastsApi.list({ pageSize: 50 })
      .then((r) => { if (!cancelled) setRows(r.data); })
      .catch((e) => { if (!cancelled) toast.error(e instanceof ApiError ? e.message : "Failed to load broadcasts"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const count = (fn: (n: PlatformBroadcast) => boolean) => rows.filter(fn).length;
  const tiles = [
    { label: "Sent", value: count((n) => n.status === "SENT"), tone: "text-success" },
    { label: "Scheduled", value: count((n) => n.status === "SCHEDULED"), tone: "text-primary" },
    { label: "Total delivered", value: rows.reduce((s, n) => s + n.deliveredCount, 0), tone: "" },
    { label: "Critical", value: count((n) => n.severity === "CRITICAL"), tone: "text-destructive" },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.label}</div>
            <div className={`mt-1 font-display text-xl font-bold tabular-nums ${t.tone}`}>{t.value.toLocaleString()}</div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /><CardTitle className="text-base">Platform broadcasts</CardTitle></div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-gradient-to-r from-accent to-primary hover:opacity-90"><Plus className="mr-1.5 h-4 w-4" /> New broadcast</Button>
              </DialogTrigger>
              <BroadcastDialog onClose={() => setOpen(false)} onSent={() => { setOpen(false); setReloadKey((k) => k + 1); }} />
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="divide-y">
          {loading ? (
            <div className="flex h-24 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No broadcasts yet. Create one to notify tenants.</div>
          ) : rows.map((n) => (
            <div key={n.id} className="flex items-start gap-3 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
                {n.severity === "CRITICAL" ? <AlertTriangle className="h-4 w-4 text-destructive" /> : n.status === "SCHEDULED" ? <Calendar className="h-4 w-4 text-primary" /> : <Bell className="h-4 w-4 text-muted-foreground" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-medium">{n.title}</div>
                  <Badge variant="outline" className={sevStyle[n.severity]}>{n.severity}</Badge>
                  <Badge variant="outline" className="text-[10px]">{scopeLabel[n.scope]}{n.targetPlan ? `: ${tierToLabel(n.targetPlan)}` : ""}</Badge>
                  <Badge variant="outline" className={statusStyle(n.status)}>{n.status}</Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{n.body}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[11px] text-muted-foreground">
                  <span>{n.scheduledAt ? `Scheduled ${new Date(n.scheduledAt).toLocaleString()}` : n.sentAt ? `Sent ${new Date(n.sentAt).toLocaleString()}` : new Date(n.createdAt).toLocaleString()}</span>
                  {n.deliveredCount > 0 && <span>· Delivered to {n.deliveredCount} admin{n.deliveredCount === 1 ? "" : "s"}</span>}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function BroadcastDialog({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState<BroadcastScope>("ALL_TENANTS");
  const [severity, setSeverity] = useState<BroadcastSeverity>("INFO");
  const [targetPlan, setTargetPlan] = useState<BackendPlanTier>("PRO");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (title.trim().length < 2 || body.trim().length < 2) {
      toast.error("Add a title and message");
      return;
    }
    setSending(true);
    try {
      const res = await platformBroadcastsApi.create({
        title: title.trim(),
        body: body.trim(),
        scope,
        severity,
        targetPlan: scope === "PLAN_TARGETED" ? targetPlan : undefined,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      });
      toast.success(res.status === "SCHEDULED" ? "Broadcast scheduled" : `Broadcast sent to ${res.deliveredCount} admin${res.deliveredCount === 1 ? "" : "s"}`);
      onSent();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Create platform broadcast</DialogTitle>
        <DialogDescription>Delivered to the targeted tenants' admin users as an in-app notification.</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 py-2">
        <div className="grid gap-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Scheduled maintenance · ..." /></div>
        <div className="grid gap-1.5"><Label>Message</Label><Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Brief description..." rows={3} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label>Audience</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as BroadcastScope)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL_TENANTS">All tenants</SelectItem>
                <SelectItem value="PLAN_TARGETED">By plan</SelectItem>
                <SelectItem value="INTERNAL">Internal only (record)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as BroadcastSeverity)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INFO">Info</SelectItem>
                <SelectItem value="WARNING">Warning</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {scope === "PLAN_TARGETED" && (
          <div className="grid gap-1.5">
            <Label>Plan tier</Label>
            <Select value={targetPlan} onValueChange={(v) => setTargetPlan(v as BackendPlanTier)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{tierToLabel(t)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="grid gap-1.5"><Label>Schedule (optional)</Label><Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={send} disabled={sending}>{sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} {scheduledAt ? "Schedule" : "Send broadcast"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
